import { For, Show, Suspense } from "solid-js";
import { A, createAsync, type RouteDefinition } from "@solidjs/router";
import { AppShell, PageHeader } from "~/components/layout";
import { Card, CardContent, CardHeader, CardTitle, Badge, Button, Skeleton } from "~/components/ui";
import { getTodaysBriefing, getTrendingTopics, getInboxStats, getPredictions } from "~/server/bff";
import { formatRelativeTime } from "~/lib/utils";
import {
  Zap,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  FileText,
  Target,
  Inbox,
  RefreshCw,
} from "lucide-solid";

export const route = {
  preload: () =>
    Promise.all([
      getTodaysBriefing(),
      getTrendingTopics(),
      getInboxStats(),
      getPredictions({ status: "pending" }),
    ]),
} satisfies RouteDefinition;

function BriefingCard() {
  const briefing = createAsync(() => getTodaysBriefing(), { deferStream: true });

  return (
    <Card>
      <CardHeader class="pb-3">
        <CardTitle class="flex items-center gap-2 text-lg">
          <FileText class="h-5 w-5" />
          Today's Briefing
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Suspense fallback={<BriefingSkeleton />}>
          <Show when={briefing()} fallback={<BriefingSkeleton />}>
            {(data) => (
              <div class="space-y-4">
                <div>
                  <h4 class="text-sm font-medium text-muted-foreground mb-2">Top Stories</h4>
                  <Show
                    when={data().topStories && data().topStories.length > 0}
                    fallback={
                      <p class="text-sm text-muted-foreground italic py-2">
                        No stories yet. Add channels in <A href="/settings/sources" class="text-primary hover:underline">Settings → Sources</A> to get started.
                      </p>
                    }
                  >
                    <ul class="space-y-2">
                      <For each={data().topStories}>
                        {(story) => (
                          <li>
                            <A
                              href={`/video/${story.videoId}`}
                              class="block rounded-md p-2 hover:bg-accent"
                            >
                              <p class="font-medium text-sm">{story.title}</p>
                              <p class="text-xs text-muted-foreground mt-0.5">{story.summary}</p>
                            </A>
                          </li>
                        )}
                      </For>
                    </ul>
                  </Show>
                </div>

                <div>
                  <h4 class="text-sm font-medium text-muted-foreground mb-2">Key Consensus</h4>
                  <Show
                    when={data().consensusPoints && data().consensusPoints.length > 0}
                    fallback={
                      <p class="text-sm text-muted-foreground italic py-1">
                        No consensus points identified yet.
                      </p>
                    }
                  >
                    <ul class="space-y-1 text-sm">
                      <For each={data().consensusPoints}>
                        {(point) => (
                          <li class="flex items-start gap-2">
                            <span class="text-green-500 mt-0.5">+</span>
                            {point}
                          </li>
                        )}
                      </For>
                    </ul>
                  </Show>
                </div>

                <div>
                  <h4 class="text-sm font-medium text-muted-foreground mb-2">Key Divergence</h4>
                  <Show
                    when={data().divergencePoints && data().divergencePoints.length > 0}
                    fallback={
                      <p class="text-sm text-muted-foreground italic py-1">
                        No diverging viewpoints detected yet.
                      </p>
                    }
                  >
                    <ul class="space-y-1 text-sm">
                      <For each={data().divergencePoints}>
                        {(point) => (
                          <li class="flex items-start gap-2">
                            <span class="text-yellow-500 mt-0.5">~</span>
                            {point}
                          </li>
                        )}
                      </For>
                    </ul>
                  </Show>
                </div>
              </div>
            )}
          </Show>
        </Suspense>
      </CardContent>
    </Card>
  );
}

function BriefingSkeleton() {
  return (
    <div class="space-y-4">
      <Skeleton class="h-4 w-24" />
      <Skeleton class="h-16 w-full" />
      <Skeleton class="h-16 w-full" />
    </div>
  );
}

function NeedsAttentionCard() {
  const inboxStats = createAsync(() => getInboxStats(), { deferStream: true });
  const predictions = createAsync(() => getPredictions({ status: "pending" }), { deferStream: true });

  const hasItems = () => {
    const stats = inboxStats();
    const preds = predictions();
    return (stats && (stats.flash > 0 || stats.high > 0)) || (preds && preds.length > 0);
  };

  return (
    <Card>
      <CardHeader class="pb-3">
        <CardTitle class="flex items-center gap-2 text-lg">
          <AlertTriangle class="h-5 w-5 text-yellow-500" />
          Needs Attention
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Suspense fallback={<AttentionSkeleton />}>
          <Show
            when={hasItems()}
            fallback={
              <div class="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <span class="text-green-500">✓</span>
                All caught up! No urgent items.
              </div>
            }
          >
            <div class="space-y-3">
              <Show when={inboxStats()}>
                {(stats) => (
                  <>
                    <Show when={stats().flash > 0}>
                      <A href="/inbox?priority=flash" class="flex items-center justify-between p-2 rounded-md hover:bg-accent">
                        <div class="flex items-center gap-2">
                          <Zap class="h-4 w-4 text-red-500" />
                          <span class="text-sm">{stats().flash} FLASH videos untriaged</span>
                        </div>
                        <ArrowRight class="h-4 w-4 text-muted-foreground" />
                      </A>
                    </Show>
                    <Show when={stats().high > 0}>
                      <A href="/inbox?priority=high" class="flex items-center justify-between p-2 rounded-md hover:bg-accent">
                        <div class="flex items-center gap-2">
                          <AlertTriangle class="h-4 w-4 text-orange-500" />
                          <span class="text-sm">{stats().high} HIGH priority videos</span>
                        </div>
                        <ArrowRight class="h-4 w-4 text-muted-foreground" />
                      </A>
                    </Show>
                  </>
                )}
              </Show>

              <Show when={predictions() && predictions()!.length > 0}>
                <A href="/track/predictions?status=due_soon" class="flex items-center justify-between p-2 rounded-md hover:bg-accent">
                  <div class="flex items-center gap-2">
                    <Target class="h-4 w-4 text-blue-500" />
                    <span class="text-sm">{predictions()!.length} predictions due soon</span>
                  </div>
                  <ArrowRight class="h-4 w-4 text-muted-foreground" />
                </A>
              </Show>
            </div>
          </Show>
        </Suspense>
      </CardContent>
    </Card>
  );
}

function AttentionSkeleton() {
  return (
    <div class="space-y-3">
      <Skeleton class="h-10 w-full" />
      <Skeleton class="h-10 w-full" />
    </div>
  );
}

function TrendingTopicsCard() {
  const trending = createAsync(() => getTrendingTopics(), { deferStream: true });

  return (
    <Card>
      <CardHeader class="pb-3">
        <CardTitle class="flex items-center gap-2 text-lg">
          <TrendingUp class="h-5 w-5" />
          Trending Topics
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Suspense fallback={<TrendingSkeleton />}>
          <Show when={trending()} fallback={<TrendingSkeleton />}>
            {(topics) => (
              <Show
                when={topics() && topics().length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground italic py-2">
                    No trending topics yet. Topics will appear as you add more content.
                  </p>
                }
              >
                <div class="space-y-2">
                  <For each={topics()}>
                    {(topic) => (
                      <A
                        href={`/explore/topics/${topic.id}`}
                        class="flex items-center justify-between p-2 rounded-md hover:bg-accent"
                      >
                        <div class="flex items-center gap-3">
                          <Show
                            when={topic.trend === "up"}
                            fallback={
                              <Show
                                when={topic.trend === "down"}
                                fallback={<div class="h-4 w-4" />}
                              >
                                <TrendingDown class="h-4 w-4 text-red-500" />
                              </Show>
                            }
                          >
                            <TrendingUp class="h-4 w-4 text-green-500" />
                          </Show>
                          <div>
                            <p class="text-sm font-medium">{topic.name}</p>
                            <p class="text-xs text-muted-foreground">
                              {topic.videoCount} videos, {topic.channelCount} creators
                            </p>
                          </div>
                        </div>
                        <Show when={topic.trend !== "stable"}>
                          <Badge variant={topic.trend === "up" ? "success" : "destructive"}>
                            {topic.trend === "up" ? "+" : "-"}{topic.percentChange}%
                          </Badge>
                        </Show>
                      </A>
                    )}
                  </For>
                </div>
              </Show>
            )}
          </Show>
        </Suspense>
      </CardContent>
    </Card>
  );
}

function TrendingSkeleton() {
  return (
    <div class="space-y-2">
      <Skeleton class="h-12 w-full" />
      <Skeleton class="h-12 w-full" />
      <Skeleton class="h-12 w-full" />
    </div>
  );
}

function QuickActionsCard() {
  return (
    <Card>
      <CardHeader class="pb-3">
        <CardTitle class="text-lg">Quick Actions</CardTitle>
      </CardHeader>
      <CardContent>
        <div class="grid grid-cols-2 gap-2">
          <A href="/inbox">
            <Button variant="outline" class="w-full justify-start">
              <Inbox class="h-4 w-4 mr-2" />
              Open Inbox
            </Button>
          </A>
          <A href="/track/predictions">
            <Button variant="outline" class="w-full justify-start">
              <Target class="h-4 w-4 mr-2" />
              Review Predictions
            </Button>
          </A>
          <A href="/explore/search">
            <Button variant="outline" class="w-full justify-start">
              <FileText class="h-4 w-4 mr-2" />
              Search Content
            </Button>
          </A>
          <A href="/investigate/analysis">
            <Button variant="outline" class="w-full justify-start">
              <RefreshCw class="h-4 w-4 mr-2" />
              Run Analysis
            </Button>
          </A>
        </div>
      </CardContent>
    </Card>
  );
}

export default function HomePage() {
  return (
    <AppShell title="Home">
      <div class="p-6">
        <PageHeader
          title="Dashboard"
          description="Your intelligence overview"
        />

        <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <div class="lg:col-span-2 stagger-item">
            <BriefingCard />
          </div>
          <div class="space-y-6">
            <div class="stagger-item">
              <NeedsAttentionCard />
            </div>
            <div class="stagger-item">
              <QuickActionsCard />
            </div>
          </div>
          <div class="lg:col-span-2 stagger-item">
            <TrendingTopicsCard />
          </div>
        </div>
      </div>
    </AppShell>
  );
}
